import { entryKeys } from '@taler/api-client'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { toContactHistoryRows, type ContactHistoryRow } from '../internal/history.ts'

export const CONTACT_HISTORY_PAGE_SIZE = 25

export interface UseContactHistoryOptions {
  readonly ledgerId: () => string | undefined
  /** The contact's backing account; absent until the contact has loaded. */
  readonly accountId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseContactHistory {
  readonly rows: ComputedRef<readonly ContactHistoryRow[]>
  readonly isLoading: ComputedRef<boolean>
  readonly isFetchingNextPage: ComputedRef<boolean>
  readonly hasNextPage: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  fetchNextPage(): void
  retry(): void
}

export function useContactHistory(options: UseContactHistoryOptions): UseContactHistory {
  const api = useApi()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.accountId() !== undefined,
  )

  const query = useInfiniteQuery({
    queryKey: computed(() =>
      entryKeys.list(options.ledgerId() ?? '', {
        limit: CONTACT_HISTORY_PAGE_SIZE,
        accountId: options.accountId() ?? '',
      }),
    ),
    queryFn: ({ pageParam, signal }) =>
      api.entries.list(
        options.ledgerId()!,
        {
          limit: CONTACT_HISTORY_PAGE_SIZE,
          accountId: options.accountId()!,
          ...(pageParam === undefined ? {} : { cursor: pageParam as string }),
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: active,
    retry: false,
  })

  return {
    rows: computed(() => {
      const accountId = options.accountId()
      if (accountId === undefined) return []
      const entries = (query.data.value?.pages ?? []).flatMap((page) => page.items)
      return toContactHistoryRows(entries, accountId)
    }),
    isLoading: computed(() => active.value && query.isPending.value),
    isFetchingNextPage: computed(() => query.isFetchingNextPage.value),
    hasNextPage: computed(() => query.hasNextPage.value),
    error: computed(() => query.error.value),
    retrying: retrying(query),
    fetchNextPage: () => {
      void query.fetchNextPage()
    },
    retry: () => {
      void query.refetch()
    },
  }
}
